/**
 * Utility functions for handling pagination errors
 */

export type PaginationErrorType = 
  | 'NETWORK_ERROR'
  | 'TIMEOUT_ERROR' 
  | 'GRAPHQL_ERROR'
  | 'UNKNOWN_ERROR';

export interface PaginationError {
  type: PaginationErrorType;
  message: string;
  originalError: Error;
  retryable: boolean;
}

/**
 * Classifies and standardizes pagination errors
 */
export const classifyPaginationError = (error: Error): PaginationError => {
  // Network errors (fetch failures, timeouts)
  if (error.name === 'TypeError' && error.message.includes('fetch')) {
    return {
      type: 'NETWORK_ERROR',
      message: 'Unable to load messages. Please check your connection.',
      originalError: error,
      retryable: true,
    };
  }

  // Timeout errors
  if (error.name === 'TimeoutError' || error.message.includes('timeout')) {
    return {
      type: 'TIMEOUT_ERROR', 
      message: 'Request timed out. Please try again.',
      originalError: error,
      retryable: true,
    };
  }

  // GraphQL errors
  if (error.message.includes('GraphQL')) {
    return {
      type: 'GRAPHQL_ERROR',
      message: 'Server error while loading messages.',
      originalError: error,
      retryable: true,
    };
  }

  // Unknown errors
  return {
    type: 'UNKNOWN_ERROR',
    message: 'An unexpected error occurred while loading messages.',
    originalError: error,
    retryable: true,
  };
};

/**
 * Calculates retry delay with exponential backoff
 */
export const calculateRetryDelay = (
  retryCount: number, 
  baseDelay = 1000,
  maxDelay = 10000
): number => {
  const delay = baseDelay * Math.pow(2, retryCount);
  return Math.min(delay, maxDelay);
};

/**
 * Determines if an error should trigger a retry
 */
export const shouldRetryError = (error: PaginationError, retryCount: number, maxRetries: number): boolean => {
  return error.retryable && retryCount < maxRetries;
};